import { Box, Button, Checkbox, Grid, Group, LoadingOverlay, Stack } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { LeavePageNotification } from '@components';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { ExcludedList } from './ExcludedList';
import classes from './ManageAccount.module.css';

const validationSettingsSchema = z.object({
  validationSettings: z.object({
    h7ExcludedCodesValidationEnabled: z.boolean(),
    IOSSExcludedValidationEnabled: z.boolean(),
    IOSSBlackList: z.array(z.string()),
    VatEoriExcludedValidationEnabled: z.boolean(),
    SameUCRValidationEnabled: z.boolean(),
    VatEoriBlackList: z.array(z.string()),
    YListEnabled: z.boolean(),
    YList: z.array(z.string()),
    b2cThresholdEnabled: z.boolean(),
  }),
});

export const ValidationSettingsForm = () => {
  const { data: settings } = useSettings();
  const { data: declarant, isLoading } = useDeclarant();
  const updateDeclarantMutation = useUpdateDeclarant();

  const buildInitialValues = (dec) => ({
    validationSettings: {
      h7ExcludedCodesValidationEnabled:
        dec.validationSettings?.h7ExcludedCodesValidationEnabled !== undefined ? dec.validationSettings.h7ExcludedCodesValidationEnabled : true,
      IOSSExcludedValidationEnabled: dec.validationSettings?.IOSSExcludedValidationEnabled || false,
      IOSSBlackList: dec.validationSettings?.IOSSBlackList || [],
      VatEoriExcludedValidationEnabled: dec.validationSettings?.VatEoriExcludedValidationEnabled || false,
      SameUCRValidationEnabled: dec.validationSettings?.SameUCRValidationEnabled || false,
      VatEoriBlackList: dec.validationSettings?.VatEoriBlackList || [],
      YListEnabled: dec.validationSettings?.YListEnabled || false,
      YList: dec.validationSettings?.YList || [],
      b2cThresholdEnabled: dec.validationSettings?.b2cThresholdEnabled || false,
    },
  });

  const onSubmit = async (values) => {
    updateDeclarantMutation.mutate({
      declarantId: declarant._id,
      declarant: values,
    });
  };

  const form = useForm({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(validationSettingsSchema),
  });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({} as any);
  }, [declarant]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="md" mb="xl">
          <Stack gap="xs">
            <Box className={classes.checkboxContainer}>
              <Checkbox
                {...form.getInputProps('validationSettings.h7ExcludedCodesValidationEnabled')}
                label="H7 excluded codes validation enabled"
                checked={form.values.validationSettings.h7ExcludedCodesValidationEnabled}
                classNames={{ label: classes.checkboxLabel }}
              />
              <p className={classes.details}>Disable H7 codes validation when creating new declarations</p>
            </Box>
          </Stack>
          <Stack gap="xs">
            <Box className={classes.checkboxContainer}>
              <Checkbox
                {...form.getInputProps('validationSettings.IOSSExcludedValidationEnabled')}
                label="Excluded IOSS validation enabled"
                checked={form.values.validationSettings.IOSSExcludedValidationEnabled}
                classNames={{ label: classes.checkboxLabel }}
              />
              <p className={classes.details}>Disable IOSS validation when creating new declarations</p>
            </Box>
          </Stack>
          <Stack gap="xs">
            <Box className={classes.checkboxContainer}>
              <Checkbox
                {...form.getInputProps('validationSettings.VatEoriExcludedValidationEnabled')}
                label="Excluded VAT/EORI validation enabled"
                checked={form.values.validationSettings.VatEoriExcludedValidationEnabled}
                classNames={{ label: classes.checkboxLabel }}
              />
              <p className={classes.details}> Disable VAT/EORI validation when uploading high value batches</p>
            </Box>
          </Stack>
          {!settings?.isForwarder && (
            <>
              <Stack gap="xs">
                <Box className={classes.checkboxContainer}>
                  <Checkbox
                    {...form.getInputProps('validationSettings.YListEnabled')}
                    label="Additional reference list enabled"
                    checked={form.values.validationSettings.YListEnabled}
                    classNames={{ label: classes.checkboxLabel }}
                  />
                  <p className={classes.details}>Generate documents with the additional references provided in the list when creating new declarations</p>
                </Box>
              </Stack>
              <Stack gap="xs">
                <Box className={classes.checkboxContainer}>
                  <Checkbox
                    {...form.getInputProps('validationSettings.SameUCRValidationEnabled')}
                    label="Excluded same UCR validation enabled"
                    checked={form.values.validationSettings.SameUCRValidationEnabled}
                    classNames={{ label: classes.checkboxLabel }}
                  />
                  <p className={classes.details}> Disable same UCR validation when uploading high value batches</p>
                </Box>
              </Stack>
            </>
          )}
          <Stack gap="xs">
            <Box className={classes.checkboxContainer}>
              <Checkbox
                {...form.getInputProps('validationSettings.b2cThresholdEnabled')}
                label={`Excluded B2C High value declaration below ${settings.lvInvoiceTreshold}€ enabled (import only)`}
                checked={form.values.validationSettings.b2cThresholdEnabled}
                classNames={{ label: classes.checkboxLabel }}
              />
              <p className={classes.details}>
                Disable verification that the total value of a declaration is less or equal than {settings.lvInvoiceTreshold}€ for a B2C HV (import only)
              </p>
            </Box>
          </Stack>
          <Grid>
            {form.values.validationSettings.IOSSExcludedValidationEnabled && (
              <Grid.Col span={3.5}>
                <ExcludedList
                  name="IOSS Code"
                  inputPlaceholder="IOSS Number"
                  list={form.values.validationSettings.IOSSBlackList}
                  onUpdate={(list: string[]) => form.setFieldValue('validationSettings.IOSSBlackList', list)}
                />
              </Grid.Col>
            )}
            {!settings?.isForwarder && form.values.validationSettings.YListEnabled && (
              <Grid.Col span={3.5}>
                <ExcludedList
                  name="Additional reference"
                  inputPlaceholder="Additional reference"
                  list={form.values.validationSettings.YList}
                  onUpdate={(list: string[]) => form.setFieldValue('validationSettings.YList', list)}
                />
              </Grid.Col>
            )}
            {form.values.validationSettings.VatEoriExcludedValidationEnabled && (
              <Grid.Col span={3.5}>
                <ExcludedList
                  name="VAT/EORI Code"
                  inputPlaceholder="VAT/EORI Number"
                  list={form.values.validationSettings.VatEoriBlackList}
                  onUpdate={(list: string[]) => form.setFieldValue('validationSettings.VatEoriBlackList', list)}
                />
              </Grid.Col>
            )}
          </Grid>
        </Stack>

        <Group>
          <Button type="submit" variant="filled" size="lg">
            Save
          </Button>
        </Group>
      </form>
    </>
  );
};
